import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useEffect, useState, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { PMField } from "../../../utils/PMField";
import { debounce } from "lodash";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ResourceError } from "../../../utils/ResourceError";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import AbsenceTypeModal from "./modal";
import axios from "../../../../api/axios";
export default function AbsenceTypesStacklist(props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState();
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const FETCH_URL = `/absences/types/`;
  const CREATE_URL = `/absences/types/`;

  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [items, setItems] = useState([]);

  const [query, setQuery] = useState();

  const [errors, setErrors] = useState([]);
  const [error, setError] = useState(null);

  const handleItemClick = (itemId) => {
    if (!itemId) {
      toast.error("Missing Item ID");
      return;
    }
    setSelectedItem(itemId);
    setModalOpen(true);
  };

  const handleOnClose = () => {
    setModalOpen(false);
    setSelectedItem(null);
  };
  const queryRef = useRef(query);
  
  // Update ref when query changes
  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const getItems = useCallback(() => {
    setLoading(true);
    const params = {
      query: queryRef.current ? queryRef.current : "",
      page: 1,
      sortBy: "created_at",
      sort: "desc",
      // filters: filters,
      view: null,
      fields: "id,displayTitle,title,description",
    };

    return axios({ method: "get", url: FETCH_URL, params: params })
      .then((response) => {
        if (response.data.data) {
          setItems(response.data.data);
          setIsLoading(false);
          setLoading(false);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setLoading(false);
        if (error.response) {
          return error.response.data;
        }
        return null;
      });
  }, []);

  const debouncedGetItemsRef = useRef(
    debounce(() => {
      getItems();
    }, 500)
  );

  // Initial load when component mounts
  useEffect(() => {
    setIsLoading(true);
    getItems();
  }, [getItems]);

  // Debounced fetch when query changes (skip initial mount)
  const isInitialMount = useRef(true);
  useEffect(() => {
    // Skip the first render (initial mount)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Only fetch when query actually changes (not on initial mount)
    setIsLoading(true);
    debouncedGetItemsRef.current();

    // Clean the debounced function on component unmount.
    return () => {
      debouncedGetItemsRef.current.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const renderItems = () => {
    return items?.map((item) => (
      <li key={item.id} onClick={() => handleItemClick(item.id)}>
        <a className="block hover:bg-gray-50">
          <div className="flex items-center px-4 py-4 sm:px-6">
            <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
              <div className="truncate">
                <div className="flex text-sm">
                  {/* <p className="ml-1 flex-shrink-0 font-normal text-gray-500">{item.storage_number}</p> */}
                  <p className="truncate font-medium text-indigo-600">
                    {item.title || "Missing title"}
                  </p>
                </div>
                <div className="flex">
                  <div className="flex items-center text-sm text-gray-500">
                    {/* <CalendarIcon className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" /> */}
                    <p>{item.description}</p>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex-shrink-0 sm:mt-0 sm:ml-5"></div>
            </div>
            <div className="ml-5 flex-shrink-0">
              <ChevronRightIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </div>
          </div>
        </a>
      </li>
    ));
  };

  return (
    <>
      <div className="mt-5">
        <button
          type="button"
          className="ml-5 flex-shrink-0 flex"
          onClick={() => setModalOpen(true)}
        >
          <PlusCircleIcon
            className="h-6 w-4 text-gray-400 hover:text-sky-700"
            aria-hidden="true"
          />
          <span className="ml-1"> Ny type</span>
        </button>
        <div className="bg-white rounded-md px-4 py-2">
          <PMField
            label="Søk type..."
            onChange={(e) => setQuery(e?.target?.value)}
            value={query}
          />
        </div>
        <div className="mt-1 overflow-auto bg-white shadow sm:rounded-md h-[400px] max-h-[400px]">
          <ul role="list" className="divide-y divide-gray-200">
            {isLoading ? (
              <div className="p-5">
                <ResourceLoading />
              </div>
            ) : error && errors ? (
              <div className="p-5">
                <ResourceError errors={errors} />
              </div>
            ) : items && items.length > 0 ? (
              renderItems()
            ) : (
              <div className="p-5">
                <ResourceEmpty />
              </div>
            )}
          </ul>
        </div>
      </div>
      <AbsenceTypeModal
        onClose={() => handleOnClose()}
        open={modalOpen}
        itemId={selectedItem}
        onRefresh={(data) =>
          // find and update or add data to items
          setItems((prevItems) => {
            const existingItemIndex = prevItems.findIndex(
              (item) => item.id === data.id
            );
            if (existingItemIndex !== -1) {
              // Update existing item
              const updatedItems = [...prevItems];
              updatedItems[existingItemIndex] = data;
              return updatedItems;
            } else {
              // Add new item
              return [...prevItems, data];
            }
          })
        }
      />
    </>
  );
}
